import {
    airportContextSwitchDisabled,
    AosAirport,
    MainAirport,
} from '../../flightInformation/types/AosAirport'
import { WeatherUnit } from '../../statusDashboard/types/filters/WeatherUnit'
import { AosUserLocalisation } from '../types/AosUserLocalisation'
import { useUserProfileQuery } from './useUserProfileQuery'

export interface UserPreferences {
    siteLocation: AosAirport
    localisation: AosUserLocalisation
    windSpeedUnit: WeatherUnit
}

// Built from `TRANSLATIONS_LANGUAGES` at build time. Used to guard against a
// stored `localisation` value that no longer maps to a loaded i18next bundle
// (e.g. a Swedavia user with cached `fi_FI` after the Phase 2 env flip to
// `en_US,sv_SE`). Without this guard, `i18next.changeLanguage('fi_FI')` lands
// on an unloaded language and `parseMissingKeyHandler` returns raw keys for
// the entire UI. The first build-time-supported language is the fallback,
// which is `en_US` in every tenant.
const SUPPORTED_LANGUAGES = new Set(Object.values(AosUserLocalisation))
const FALLBACK_LANGUAGE = (Object.values(AosUserLocalisation)[0] ?? 'en_US') as AosUserLocalisation

function resolveLocalisation(raw: string | null | undefined): AosUserLocalisation {
    if (raw && SUPPORTED_LANGUAGES.has(raw)) {
        return raw as AosUserLocalisation
    }
    return FALLBACK_LANGUAGE
}

// A stored airport written before the tenant locked the context must not keep a
// user on a site that tenant never onboarded, so the lock ignores it.
const resolveSiteLocation = (stored: AosAirport | undefined): AosAirport =>
    airportContextSwitchDisabled ? MainAirport : (stored ?? MainAirport)

export const usePreferences = (userId: number | undefined) => {
    const { data, isLoading, isError } = useUserProfileQuery(userId)
    const preferences: UserPreferences = {
        siteLocation: resolveSiteLocation(data?.siteLocation),
        localisation: resolveLocalisation(data?.localisation),
        windSpeedUnit: data?.windSpeedUnit ?? WeatherUnit.Ms,
    }
    return {
        preferences,
        siteLocation: preferences.siteLocation,
        localisation: preferences.localisation,
        windSpeedUnit: preferences.windSpeedUnit,
        isLoading,
        isError,
        // `isResolved` = the user's real preferences are known. State-writing
        // consumers (session bridge, language sync) must wait for this.
        isResolved: !!data,
        // `isFallback` = the profile fetch failed and `preferences` carries the
        // MainAirport/default-language fallbacks. Read-only queries may opt in
        // with `enabled: isResolved || isFallback` instead of staying disabled.
        isFallback: isError && !data,
    }
}
